import React, { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import {
  Feature,
  CreateFeatureRequest,
  FeatureCostType,
  FeatureCostApplication,
  PercentageType,
  FeatureType,
} from '../../types';
import { useViewPreference } from '../../hooks/useViewPreference';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CogIcon,
  UserIcon,
  CubeIcon,
  Squares2X2Icon,
  TableCellsIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { StripeIndicator } from '../../components/StripeIndicator';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { ToastContainer } from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

// Helper function to extract error messages
const getErrorMessage = (error: unknown): string | null => {
  if (error && typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    if (errorObj.response && typeof errorObj.response === 'object') {
      const response = errorObj.response as Record<string, unknown>;
      if (response.data && typeof response.data === 'object') {
        const data = response.data as Record<string, unknown>;
        if (typeof data.message === 'string') {
          return data.message;
        }
      }
    }
    if (typeof errorObj.message === 'string') {
      return errorObj.message;
    }
  }
  return null;
};

export const FeaturesPage: React.FC = () => {
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFeature, setEditingFeature] = useState<Feature | null>(null);
  const [creating, setCreating] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [filter, setFilter] = useState<{ category: string; type: string; active: string }>({
    category: 'all',
    type: 'all',
    active: 'all',
  });
  const [sort, setSort] = useState<{ field: string; direction: 'asc' | 'desc' }>({
    field: 'name',
    direction: 'asc',
  });
  const [viewMode, setViewMode] = useViewPreference('features', 'grid');
  const [formKey, setFormKey] = useState(0);

  // Stripe actions UI state
  const [stripeDropdownOpen, setStripeDropdownOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [purgeLoading, setPurgeLoading] = useState(false);

  // Toasts
  const { toasts, removeToast, showSuccess, showError, showWarning } = useToast();

  const [formData, setFormData] = useState<CreateFeatureRequest>({
    name: '',
    displayName: '',
    description: '',
    category: 'core',
    type: 'boolean',
    pricing: {
      defaultCostType: 'flat_fee',
      basePrice: 0, // Add the required basePrice field
      flatFee: {
        application: 'per_plan',
        costs: {
          daily: 0,
          monthly: 0,
          yearly: 0,
        },
        currency: 'GBP',
      },
      percentageFee: {
        type: 'percentage_of_plan',
        percentage: 0,
      },
      overagePrice: 0,
    },
    config: {
      isCore: false,
      isVisible: true,
      requiresSetup: false,
      minQuantity: 0,
      maxQuantity: 1000,
      defaultQuantity: 1,
    },
  });

  const types = ['boolean', 'usage', 'per_user', 'per_item'];

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'boolean':
        return <CogIcon className="h-4 w-4" />;
      case 'usage':
        return <ChartBarIcon className="h-4 w-4" />;
      case 'per_user':
        return <UserIcon className="h-4 w-4" />;
      case 'per_item':
        return <CubeIcon className="h-4 w-4" />;
      default:
        return <CogIcon className="h-4 w-4" />;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'core':
        return 'bg-blue-100 text-blue-800';
      case 'analytics':
        return 'bg-green-100 text-green-800';
      case 'integrations':
        return 'bg-purple-100 text-purple-800';
      case 'support':
        return 'bg-yellow-100 text-yellow-800';
      case 'security':
        return 'bg-red-100 text-red-800';
      case 'customization':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const data = await api.getFeatures();
        console.log('Fetched features data:', data);
        const featuresArray = Array.isArray(data) ? data : [];
        setFeatures(featuresArray);

        // Extract unique categories from features (with defensive checks)
        const uniqueCategories = Array.from(
          new Set(
            featuresArray.map((f) => f.category).filter((cat) => cat !== undefined && cat !== null)
          )
        );
        setCategories(uniqueCategories);
      } catch (error) {
        console.error(
          'Error fetching features:',
          error instanceof Error ? error.message : 'Unknown error'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchFeatures();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.displayName || !formData.category || !formData.type) return;

    setCreating(true);
    try {
      console.log('Submitting form data:', JSON.stringify(formData, null, 2));
      console.log('EditingFeature:', editingFeature);

      if (editingFeature) {
        console.log('Updating existing feature:', editingFeature.id);
        const updated = await api.updateFeature(editingFeature.id, formData);
        setFeatures(features.map((f) => (f.id === editingFeature.id ? updated : f)));
        setEditingFeature(null);
      } else {
        console.log('Creating new feature');
        const newFeature = await api.createFeature(formData);
        console.log('Created feature:', newFeature);
        setFeatures([...features, newFeature]);
      }

      resetForm();
      setShowCreateForm(false);
    } catch (error: unknown) {
      console.error('Error saving feature:', error);
      console.error('Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        response: (error as { response?: { data?: unknown } })?.response?.data,
        status: (error as { response?: { status?: number } })?.response?.status,
        formData: formData,
      });

      // Show more specific error message if available
      const errorMessage = getErrorMessage(error) || 'Failed to save feature. Please try again.';
      alert(errorMessage);
    } finally {
      setCreating(false);
    }
  };
  const handleEdit = (feature: Feature) => {
    setEditingFeature(feature);
    setFormData({
      name: feature.name,
      displayName: feature.displayName,
      description: feature.description,
      category: feature.category,
      type: feature.type,
      pricing: {
        defaultCostType: feature.pricing.defaultCostType,
        basePrice: feature.pricing.basePrice || 0,
        flatFee: feature.pricing.flatFee || {
          application: 'per_plan',
          costs: { daily: 0, monthly: 0, yearly: 0 },
          currency: 'GBP',
        },
        percentageFee: feature.pricing.percentageFee || {
          type: 'percentage_of_plan',
          percentage: 0,
        },
        overagePrice: feature.pricing.overagePrice,
        pricingTiers: feature.pricing.pricingTiers,
      },
      config: {
        isCore: feature.config.isCore,
        isVisible: feature.config.isVisible,
        requiresSetup: feature.config.requiresSetup,
        minQuantity: feature.config.minQuantity,
        maxQuantity: feature.config.maxQuantity,
        defaultQuantity: feature.config.defaultQuantity,
        dependsOn: feature.config.dependsOn,
        conflictsWith: feature.config.conflictsWith,
      },
    });
    setFormKey((prev) => prev + 1); // Force form re-mount
    setShowCreateForm(true);
  };

  const handleClone = (feature: Feature) => {
    // Clear editing feature since this is a clone (new feature)
    setEditingFeature(null);
    setFormData({
      name: `${feature.name}_copy`, // Add _copy suffix to avoid name conflicts
      displayName: `${feature.displayName} (Copy)`,
      description: feature.description,
      category: feature.category,
      type: feature.type,
      pricing: {
        defaultCostType: feature.pricing.defaultCostType,
        basePrice: feature.pricing.basePrice || 0,
        flatFee: feature.pricing.flatFee || {
          application: 'per_plan',
          costs: { daily: 0, monthly: 0, yearly: 0 },
          currency: 'GBP',
        },
        percentageFee: feature.pricing.percentageFee || {
          type: 'percentage_of_plan',
          percentage: 0,
        },
        overagePrice: feature.pricing.overagePrice,
        pricingTiers: feature.pricing.pricingTiers,
      },
      config: {
        isCore: false, // Cloned features are not core by default
        isVisible: feature.config.isVisible,
        requiresSetup: feature.config.requiresSetup,
        minQuantity: feature.config.minQuantity,
        maxQuantity: feature.config.maxQuantity,
        defaultQuantity: feature.config.defaultQuantity,
        dependsOn: feature.config.dependsOn,
        conflictsWith: feature.config.conflictsWith,
      },
    });
    setFormKey((prev) => prev + 1); // Force form re-mount
    setShowCreateForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this feature?')) return;

    try {
      await api.deleteFeature(id);
      setFeatures(features.filter((f) => f.id !== id));
    } catch (error) {
      console.error('Error deleting feature:', error);
      alert('Failed to delete feature. Please try again.');
    }
  };

  const handleToggleActive = async (feature: Feature) => {
    try {
      const updated = await api.updateFeature(feature.id, {
        isActive: !feature.isActive,
      });
      setFeatures(features.map((f) => (f.id === feature.id ? updated : f)));
    } catch (error) {
      console.error('Error updating feature:', error);
      alert('Failed to update feature status. Please try again.');
    }
  };

  // Add useEffect to ensure form updates when editingFeature changes
  useEffect(() => {
    if (editingFeature && showCreateForm) {
      // Force a re-render to ensure conditional panels show properly
      const timeoutId = setTimeout(() => {
        setFormData((prevData) => ({ ...prevData }));
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [editingFeature, showCreateForm]);

  // Add useEffect to scroll to form when it becomes visible
  useEffect(() => {
    if (showCreateForm) {
      // Use a small delay to ensure the form is rendered before scrolling
      const scrollTimeout = setTimeout(() => {
        const formElement = document.getElementById('feature-form');
        if (formElement) {
          // Get the element's position relative to the document
          const elementRect = formElement.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;

          // Account for sticky navbar height (64px = h-16 in Tailwind) plus some padding
          const navbarHeight = 64;
          const padding = 20; // Additional padding for better visual spacing
          const offsetPosition = absoluteElementTop - navbarHeight - padding;

          // Smooth scroll to the calculated position
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth',
          });
        }
      }, 150); // Slightly increased delay to ensure proper rendering
      return () => clearTimeout(scrollTimeout);
    }
  }, [showCreateForm]);

  // Stripe actions
  const isNotFound = (msg: string): boolean => msg.includes('404') || msg.includes('Cannot ');

  const handleSyncFeatures = async () => {
    setSyncLoading(true);
    try {
      const result = await api.syncFeatures();
      showSuccess(
        'Features Synced',
        result.message || 'Features have been synced with Stripe successfully'
      );
      // Optionally refresh status summary without reloading the page
      try {
        const statusResp = await api.getFeatureStripeStatus();
        const s = statusResp.data;
        showSuccess(
          'Stripe Status',
          `Total: ${s.totalFeatures}, Synced: ${s.stripeSynced}, Missing: ${s.missingFromStripe?.length || 0}`,
          3000
        );
      } catch (_) {
        // ignore status fetch errors
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isNotFound(msg)) {
        showWarning(
          'Sync Endpoint Missing',
          'Feature sync API is not available. Please implement a POST /api/features/sync (or adjust client to the correct path), then retry.',
          8000
        );
      } else {
        showError('Sync Failed', `Failed to sync features with Stripe: ${msg}`);
      }
    } finally {
      setSyncLoading(false);
      setStripeDropdownOpen(false);
    }
  };

  const handleCleanupStripeReferences = async () => {
    if (
      !window.confirm(
        'Clean orphaned Stripe references? This will remove references that do not exist in Stripe.'
      )
    )
      return;
    setCleanupLoading(true);
    try {
      const result = await api.cleanupOrphanedStripeReferences();
      const cleaned = result.data?.cleaned || [];
      const errors = result.data?.errors || [];
      if (cleaned.length > 0) {
        showSuccess('Cleanup Completed', `Cleaned ${cleaned.length} reference(s)`);
      } else {
        showSuccess('No Cleanup Needed', 'No orphaned references found');
      }
      if (errors.length > 0) {
        showWarning('Cleanup Warnings', `${errors.length} error(s): ${errors.join(', ')}`, 8000);
      }
      window.location.reload();
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isNotFound(msg)) {
        showWarning(
          'Cleanup Endpoint Missing',
          'Stripe cleanup API is not available. Please implement POST /api/features/cleanup (or use pricing cleanup if intended).',
          8000
        );
      } else {
        showError('Cleanup Failed', `Failed to clean Stripe references: ${msg}`);
      }
    } finally {
      setCleanupLoading(false);
      setStripeDropdownOpen(false);
    }
  };

  const handlePurgeOrphanedProducts = async () => {
    const confirmMessage =
      'Purge orphaned Stripe products? This will archive products in Stripe that are not referenced. This action cannot be undone.';
    if (!window.confirm(confirmMessage)) return;
    setPurgeLoading(true);
    try {
      const result = await api.purgeOrphanedStripeProducts();
      const purged = result.data?.purged || [];
      const errors = result.data?.errors || [];
      if (purged.length > 0) {
        showSuccess('Purge Completed', `Purged ${purged.length} product(s)`);
      } else {
        showSuccess('No Orphaned Products', 'No orphaned products to purge');
      }
      if (errors.length > 0) {
        showWarning('Purge Warnings', `${errors.length} error(s) occurred`, 8000);
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (isNotFound(msg)) {
        showWarning(
          'Purge Endpoint Missing',
          'Stripe purge API is not available. Please implement POST /api/features/purge (or use pricing purge if intended).',
          8000
        );
      } else {
        showError('Purge Failed', `Failed to purge orphaned Stripe products: ${msg}`);
      }
    } finally {
      setPurgeLoading(false);
      setStripeDropdownOpen(false);
    }
  };

  const handleShowFeatureStripeStatus = async () => {
    try {
      const result = await api.getFeatureStripeStatus();
      const status = result.data;
      showSuccess(
        'Feature Stripe Status',
        `Total: ${status.totalFeatures}, Synced: ${status.stripeSynced}, Missing: ${status.missingFromStripe?.length || 0}`
      );
      setStripeDropdownOpen(false);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'Unknown error';
      if (msg.includes('404')) {
        showWarning(
          'Status Unavailable',
          'Stripe status endpoint is unavailable or requires configuration. Try "Sync with Stripe" first, then re-check.',
          7000
        );
      } else {
        showError('Status Failed', `Failed to get feature Stripe status: ${msg}`);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      category: categories.length > 0 ? categories[0] : 'core',
      type: 'boolean',
      pricing: {
        defaultCostType: 'flat_fee',
        basePrice: 0,
        flatFee: {
          application: 'per_plan',
          costs: {
            daily: 0,
            monthly: 0,
            yearly: 0,
          },
          currency: 'GBP',
        },
        percentageFee: {
          type: 'percentage_of_plan',
          percentage: 0,
        },
        overagePrice: 0,
      },
      config: {
        isCore: false,
        isVisible: true,
        requiresSetup: false,
        minQuantity: 0,
        maxQuantity: 1000,
        defaultQuantity: 1,
      },
    });
    setEditingFeature(null);
    setFormKey((prev) => prev + 1); // Force form re-mount
  };

  const filteredFeatures = features
    .filter((feature) => {
      const categoryMatch = filter.category === 'all' || feature.category === filter.category;
      const typeMatch = filter.type === 'all' || feature.type === filter.type;
      const activeMatch =
        filter.active === 'all' ||
        (filter.active === 'active' && feature.isActive) ||
        (filter.active === 'inactive' && !feature.isActive);

      return categoryMatch && typeMatch && activeMatch;
    })
    .sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sort.field === 'name') {
        aValue = a.displayName.toLowerCase();
        bValue = b.displayName.toLowerCase();
      } else if (sort.field === 'category') {
        aValue = (a.category || '').toLowerCase();
        bValue = (b.category || '').toLowerCase();
      } else {
        return 0;
      }

      if (sort.direction === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-base-content mb-2">Features</h1>
            <p className="text-base-content/70">Create and manage feature flags and capabilities</p>
          </div>
          <div className="flex items-center space-x-3">
            {/* Stripe Actions Dropdown */}
            <div className="relative">
              <button
                onClick={() => setStripeDropdownOpen(!stripeDropdownOpen)}
                disabled={syncLoading || cleanupLoading || purgeLoading}
                className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  syncLoading || cleanupLoading || purgeLoading
                    ? 'bg-base-content/40 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Stripe Actions
                <ChevronDownIcon className="h-4 w-4 ml-2" />
              </button>
              {stripeDropdownOpen && (
                <div className="absolute z-10 mt-2 w-64 bg-base-100 rounded-md shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="py-1">
                    <button
                      onClick={handleSyncFeatures}
                      disabled={syncLoading}
                      className="w-full text-left px-4 py-2 text-sm text-base-content hover:bg-base-200 hover:text-base-content disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {syncLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600 mr-2"></div>
                      ) : (
                        <svg
                          className="h-4 w-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      )}
                      <div>
                        <div className="font-medium">
                          {syncLoading ? 'Syncing...' : 'Sync with Stripe'}
                        </div>
                        <div className="text-xs text-base-content/70">
                          Synchronise features with Stripe
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={handleCleanupStripeReferences}
                      disabled={cleanupLoading}
                      className="w-full text-left px-4 py-2 text-sm text-base-content hover:bg-base-200 hover:text-base-content disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {cleanupLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-600 mr-2"></div>
                      ) : (
                        <svg
                          className="h-4 w-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      )}
                      <div>
                        <div className="font-medium">
                          {cleanupLoading ? 'Cleaning...' : 'Clean Stripe References'}
                        </div>
                        <div className="text-xs text-base-content/70">
                          Remove orphaned Stripe references
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={handlePurgeOrphanedProducts}
                      disabled={purgeLoading}
                      className="w-full text-left px-4 py-2 text-sm text-base-content hover:bg-base-200 hover:text-base-content disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {purgeLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600 mr-2"></div>
                      ) : (
                        <svg
                          className="h-4 w-4 mr-2 text-red-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                          />
                        </svg>
                      )}
                      <div>
                        <div className="font-medium text-red-600">
                          {purgeLoading ? 'Purging...' : 'Purge Stripe Products'}
                        </div>
                        <div className="text-xs text-base-content/70">
                          Archive orphaned products in Stripe
                        </div>
                      </div>
                    </button>

                    <button
                      onClick={handleShowFeatureStripeStatus}
                      className="w-full text-left px-4 py-2 text-sm text-base-content hover:bg-base-200 hover:text-base-content flex items-center"
                    >
                      <svg
                        className="h-4 w-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      <div>
                        <div className="font-medium">Show Stripe Status</div>
                        <div className="text-xs text-base-content/70">
                          Summary of feature sync state
                        </div>
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* View Toggle */}
            <div className="flex items-center bg-base-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-md transition-colors ${
                  viewMode === 'table'
                    ? 'bg-base-100 text-base-content shadow-sm'
                    : 'text-base-content/70 hover:text-base-content'
                }`}
                title="Table view"
              >
                <TableCellsIcon className="h-4 w-4" />
              </button>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowCreateForm(true);
              }}
              disabled={showCreateForm}
              className={`${showCreateForm ? 'bg-base-content/40 cursor-not-allowed' : 'bg-primary hover:bg-primary/80'} text-white px-4 py-2 rounded-md font-medium transition-colors flex items-center`}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Feature
            </button>
          </div>
        </div>
      </div>

      {/* Filters and Sorting */}
      <div className="bg-base-100 shadow rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label
              htmlFor="categoryFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Category
            </label>
            <select
              id="categoryFilter"
              value={filter.category}
              onChange={(e) => setFilter({ ...filter, category: e.target.value })}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="typeFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Type
            </label>
            <select
              id="typeFilter"
              value={filter.type}
              onChange={(e) => setFilter({ ...filter, type: e.target.value })}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Types</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {type.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="activeFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Status
            </label>
            <select
              id="activeFilter"
              value={filter.active}
              onChange={(e) => setFilter({ ...filter, active: e.target.value })}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div>
            <label
              htmlFor="sortFilter"
              className="block text-sm font-medium text-base-content mb-1"
            >
              Sort By
            </label>
            <select
              id="sortFilter"
              value={`${sort.field}-${sort.direction}`}
              onChange={(e) => {
                const [field, direction] = e.target.value.split('-');
                setSort({ field, direction: direction as 'asc' | 'desc' });
              }}
              className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="category-asc">Category (A-Z)</option>
              <option value="category-desc">Category (Z-A)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div id="feature-form" className="bg-base-100 shadow rounded-lg p-6">
          <h2 className="text-xl font-bold text-base-content mb-4">
            {editingFeature ? 'Edit' : 'Create'} Feature
          </h2>
          <form key={formKey} onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-base-content mb-1">
                  Internal Name *
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="e.g., advanced_analytics, custom_themes"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="displayName"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Display Name *
                </label>
                <input
                  type="text"
                  id="displayName"
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  placeholder="e.g., Advanced Analytics, Custom Themes"
                  required
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-base-content mb-1"
              >
                Description *
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                rows={3}
                placeholder="Describe what this feature provides"
                required
              />
            </div>

            {/* Feature Configuration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-base-content mb-1"
                >
                  Category *
                </label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                >
                  {categories.length > 0
                    ? categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))
                    : [
                        'core',
                        'analytics',
                        'integrations',
                        'support',
                        'security',
                        'customization',
                      ].map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                </select>
              </div>

              <div>
                <label htmlFor="type" className="block text-sm font-medium text-base-content mb-1">
                  Type *
                </label>
                <select
                  id="type"
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value as FeatureType })
                  }
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="boolean">Boolean (On/Off)</option>
                  <option value="usage">Usage (Limits)</option>
                  <option value="per_user">Per User</option>
                  <option value="per_item">Per Item</option>
                </select>
              </div>
            </div>

            {/* Pricing Configuration */}
            <div>
              <h3 className="text-lg font-medium text-base-content mb-4">Pricing Configuration</h3>

              {/* Default Cost Type */}
              <div className="mb-6">
                <label
                  htmlFor="defaultCostType"
                  className="block text-sm font-medium text-base-content mb-2"
                >
                  Default Cost Type
                </label>
                <select
                  id="defaultCostType"
                  value={formData.pricing.defaultCostType}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pricing: {
                        ...formData.pricing,
                        defaultCostType: e.target.value as FeatureCostType,
                      },
                    })
                  }
                  className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="flat_fee">Flat Fee</option>
                  <option value="percentage_fee">Percentage Fee</option>
                </select>
              </div>

              {/* Flat Fee Configuration */}
              {formData.pricing.defaultCostType === 'flat_fee' && (
                <div className="mb-6 p-4 bg-base-200 rounded-lg">
                  <h4 className="text-md font-medium text-base-content mb-3">Flat Fee Settings</h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="flatFeeApplication"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Application
                      </label>
                      <select
                        id="flatFeeApplication"
                        value={formData.pricing.flatFee?.application || 'per_plan'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              flatFee: {
                                ...formData.pricing.flatFee,
                                application: e.target.value as FeatureCostApplication,
                                costs: formData.pricing.flatFee?.costs || {
                                  daily: 0,
                                  monthly: 0,
                                  yearly: 0,
                                },
                                currency: formData.pricing.flatFee?.currency || 'GBP',
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="per_plan">Per Plan</option>
                        <option value="per_user">Per User</option>
                        <option value="per_program">Per Program</option>
                        <option value="per_project">Per Project</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="flatFeeCurrency"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Currency
                      </label>
                      <select
                        id="flatFeeCurrency"
                        value={formData.pricing.flatFee?.currency || 'GBP'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              flatFee: {
                                ...formData.pricing.flatFee,
                                application: formData.pricing.flatFee?.application || 'per_plan',
                                costs: formData.pricing.flatFee?.costs || {
                                  daily: 0,
                                  monthly: 0,
                                  yearly: 0,
                                },
                                currency: e.target.value,
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="GBP">GBP (£)</option>
                        <option value="USD">USD ($)</option>
                        <option value="EUR">EUR (€)</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label
                        htmlFor="dailyCost"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Daily Cost
                      </label>
                      <input
                        type="number"
                        id="dailyCost"
                        min="0"
                        step="0.01"
                        value={formData.pricing.flatFee?.costs?.daily || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              flatFee: {
                                ...formData.pricing.flatFee,
                                application: formData.pricing.flatFee?.application || 'per_plan',
                                currency: formData.pricing.flatFee?.currency || 'GBP',
                                costs: {
                                  ...formData.pricing.flatFee?.costs,
                                  daily: parseFloat(e.target.value) || 0,
                                  monthly: formData.pricing.flatFee?.costs?.monthly || 0,
                                  yearly: formData.pricing.flatFee?.costs?.yearly || 0,
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="monthlyCost"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Monthly Cost
                      </label>
                      <input
                        type="number"
                        id="monthlyCost"
                        min="0"
                        step="0.01"
                        value={formData.pricing.flatFee?.costs?.monthly || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              flatFee: {
                                ...formData.pricing.flatFee,
                                application: formData.pricing.flatFee?.application || 'per_plan',
                                currency: formData.pricing.flatFee?.currency || 'GBP',
                                costs: {
                                  ...formData.pricing.flatFee?.costs,
                                  daily: formData.pricing.flatFee?.costs?.daily || 0,
                                  monthly: parseFloat(e.target.value) || 0,
                                  yearly: formData.pricing.flatFee?.costs?.yearly || 0,
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="yearlyCost"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Yearly Cost
                      </label>
                      <input
                        type="number"
                        id="yearlyCost"
                        min="0"
                        step="0.01"
                        value={formData.pricing.flatFee?.costs?.yearly || 0}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              flatFee: {
                                ...formData.pricing.flatFee,
                                application: formData.pricing.flatFee?.application || 'per_plan',
                                currency: formData.pricing.flatFee?.currency || 'GBP',
                                costs: {
                                  ...formData.pricing.flatFee?.costs,
                                  daily: formData.pricing.flatFee?.costs?.daily || 0,
                                  monthly: formData.pricing.flatFee?.costs?.monthly || 0,
                                  yearly: parseFloat(e.target.value) || 0,
                                },
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Percentage Fee Configuration */}
              {formData.pricing.defaultCostType === 'percentage_fee' && (
                <div className="mb-6 p-4 bg-base-200 rounded-lg">
                  <h4 className="text-md font-medium text-base-content mb-3">
                    Percentage Fee Settings
                  </h4>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="percentageType"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Percentage Type
                      </label>
                      <select
                        id="percentageType"
                        value={formData.pricing.percentageFee?.type || 'percentage_of_plan'}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              percentageFee: {
                                ...formData.pricing.percentageFee,
                                type: e.target.value as PercentageType,
                                percentage: formData.pricing.percentageFee?.percentage || 0,
                              },
                            },
                          })
                        }
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                      >
                        <option value="percentage_of_plan">Percentage of Plan</option>
                        <option value="percentage_of_user_cost">Percentage of User Cost</option>
                        <option value="percentage_of_program_cost">
                          Percentage of Program Cost
                        </option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="percentageAmount"
                        className="block text-sm font-medium text-base-content mb-1"
                      >
                        Percentage (%)
                      </label>
                      <input
                        type="number"
                        id="percentageAmount"
                        min="0"
                        max="100"
                        step="0.01"
                        value={formData.pricing.percentageFee?.percentage || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty values temporarily, but convert to number when not empty
                          let numValue: number;
                          if (value === '') {
                            numValue = 0; // Set to 0 for empty values
                          } else {
                            const parsed = parseFloat(value);
                            numValue = isNaN(parsed) ? 0 : parsed;
                          }

                          setFormData({
                            ...formData,
                            pricing: {
                              ...formData.pricing,
                              percentageFee: {
                                ...formData.pricing.percentageFee,
                                type: formData.pricing.percentageFee?.type || 'percentage_of_plan',
                                percentage: numValue,
                              },
                            },
                          });
                        }}
                        className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Overage Pricing for Usage Features */}
              {formData.type === 'usage' && (
                <div className="mt-4">
                  <label
                    htmlFor="overagePrice"
                    className="block text-sm font-medium text-base-content mb-1"
                  >
                    Overage Price (per unit)
                  </label>
                  <input
                    type="number"
                    id="overagePrice"
                    min="0"
                    step="0.01"
                    value={formData.pricing.overagePrice || 0}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        pricing: {
                          ...formData.pricing,
                          overagePrice: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="0.00"
                  />
                </div>
              )}
            </div>

            {/* Configuration */}
            <div>
              <h3 className="text-lg font-medium text-base-content mb-4">Feature Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="defaultQuantity"
                    className="block text-sm font-medium text-base-content mb-1"
                  >
                    Default Quantity
                  </label>
                  <input
                    type="number"
                    id="defaultQuantity"
                    min="0"
                    value={formData.config.defaultQuantity || 1}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          defaultQuantity: parseInt(e.target.value) || 1,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="1"
                  />
                </div>

                <div>
                  <label
                    htmlFor="maxQuantity"
                    className="block text-sm font-medium text-base-content mb-1"
                  >
                    Max Quantity
                  </label>
                  <input
                    type="number"
                    id="maxQuantity"
                    min="1"
                    value={formData.config.maxQuantity || 1000}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: {
                          ...formData.config,
                          maxQuantity: parseInt(e.target.value) || 1000,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-base-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    placeholder="1000"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-6 mt-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isCore"
                    checked={formData.config.isCore || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, isCore: e.target.checked },
                      })
                    }
                    className="h-4 w-4 text-primary focus:ring-primary border-base-300 rounded"
                  />
                  <label htmlFor="isCore" className="ml-2 block text-sm text-base-content">
                    Core Feature (cannot be disabled)
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isVisible"
                    checked={formData.config.isVisible}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, isVisible: e.target.checked },
                      })
                    }
                    className="h-4 w-4 text-primary focus:ring-primary border-base-300 rounded"
                  />
                  <label htmlFor="isVisible" className="ml-2 block text-sm text-base-content">
                    Visible in UI
                  </label>
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="requiresSetup"
                    checked={formData.config.requiresSetup || false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, requiresSetup: e.target.checked },
                      })
                    }
                    className="h-4 w-4 text-primary focus:ring-primary border-base-300 rounded"
                  />
                  <label htmlFor="requiresSetup" className="ml-2 block text-sm text-base-content">
                    Requires Setup
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={creating}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                {creating ? 'Saving...' : editingFeature ? 'Update Feature' : 'Create Feature'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  resetForm();
                }}
                className="bg-neutral hover:bg-neutral/80 text-white px-4 py-2 rounded-md font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Features List - Conditional rendering based on view mode */}
      {viewMode === 'grid' ? (
        // Grid View
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredFeatures.map((feature) => (
            <div key={feature.id} className="bg-base-100 rounded-lg shadow-md border p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    {getTypeIcon(feature.type)}
                    <h3 className="text-lg font-bold text-base-content ml-2">
                      {feature.displayName}
                    </h3>
                    <div className="ml-2">
                      <StripeIndicator
                        stripeProductId={feature.stripeProductId}
                        stripePriceId={
                          feature.pricing?.billingFrequency && feature.stripePriceIds
                            ? feature.stripePriceIds[feature.pricing.billingFrequency]
                            : undefined
                        }
                      />
                    </div>
                  </div>
                  <p className="text-sm text-base-content/70 mb-2">{feature.name}</p>
                  <p className="text-sm text-base-content/70">{feature.description}</p>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleEdit(feature)}
                    className="p-1 text-base-content/50 hover:text-blue-600 transition-colors"
                    title="Edit feature"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleClone(feature)}
                    className="p-1 text-base-content/50 hover:text-green-600 transition-colors"
                    title="Clone feature"
                  >
                    <DocumentDuplicateIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(feature.id)}
                    className="p-1 text-base-content/50 hover:text-red-600 transition-colors"
                    title="Delete feature"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(feature.category || 'unknown')}`}
                  >
                    {feature.category
                      ? feature.category.charAt(0).toUpperCase() + feature.category.slice(1)
                      : 'Unknown'}
                  </span>
                  <span className="text-xs text-base-content/70 bg-base-200 px-2 py-1 rounded">
                    {feature.type.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="border-t pt-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-base-content">Default Cost:</span>
                    <span className="text-sm text-base-content/70">
                      {feature.pricing.defaultCostType === 'flat_fee'
                        ? 'Flat Fee'
                        : 'Percentage Fee'}
                    </span>
                  </div>

                  {feature.pricing.flatFee && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-base-content">Flat Fee:</span>
                      <span className="text-sm text-base-content/70">
                        {feature.pricing.flatFee.currency}{' '}
                        {feature.pricing.flatFee.costs?.monthly?.toFixed(2) || '0.00'}/month
                        <span className="text-xs">
                          {' '}
                          ({feature.pricing.flatFee.application.replace('_', ' ')})
                        </span>
                      </span>
                    </div>
                  )}

                  {feature.pricing.percentageFee &&
                    feature.pricing.percentageFee.percentage > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-base-content">Percentage:</span>
                        <span className="text-sm text-base-content/70">
                          {feature.pricing.percentageFee.percentage}% of{' '}
                          {feature.pricing.percentageFee.type.replace('_', ' ')}
                        </span>
                      </div>
                    )}

                  {feature.type === 'usage' &&
                    feature.pricing.overagePrice &&
                    feature.pricing.overagePrice > 0 && (
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-base-content">Overage:</span>
                        <span className="text-sm text-base-content/70">
                          {feature.pricing.flatFee?.currency || 'GBP'}{' '}
                          {feature.pricing.overagePrice.toFixed(2)} per unit
                        </span>
                      </div>
                    )}

                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-base-content">Default Qty:</span>
                    <span className="text-sm text-base-content/70">
                      {feature.config.defaultQuantity || 1} (max:{' '}
                      {feature.config.maxQuantity || 'unlimited'})
                    </span>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-base-content">Status:</span>
                    <button
                      onClick={() => handleToggleActive(feature)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                        feature.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {feature.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </div>

                  {(feature.config.isCore || feature.config.requiresSetup) && (
                    <div className="flex items-center mt-2 space-x-2">
                      {feature.config.isCore && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                          Core
                        </span>
                      )}
                      {feature.config.requiresSetup && (
                        <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                          Setup Required
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-base-300 text-xs text-base-content/70">
                <div className="flex justify-between">
                  <span>Created: {new Date(feature.createdAt).toLocaleDateString()}</span>
                  {feature.updatedAt !== feature.createdAt && (
                    <span>Updated: {new Date(feature.updatedAt).toLocaleDateString()}</span>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // Table View
        <div className="bg-base-100 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-base-300">
            <thead className="bg-base-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Feature
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Pricing
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Configuration
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-base-100 divide-y divide-base-300">
              {filteredFeatures.map((feature) => (
                <tr key={feature.id} className="hover:bg-base-200">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 mr-3">{getTypeIcon(feature.type)}</div>
                      <div>
                        <div className="flex items-center space-x-2 text-sm font-medium text-base-content">
                          <span>{feature.displayName}</span>
                          <StripeIndicator
                            stripeProductId={feature.stripeProductId}
                            stripePriceId={
                              feature.stripePriceIds
                                ? Object.values(feature.stripePriceIds)[0]
                                : undefined
                            }
                          />
                        </div>
                        <div className="text-sm text-base-content/70">{feature.name}</div>
                        <div className="text-sm text-base-content/70 max-w-xs truncate">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(feature.category || 'unknown')}`}
                    >
                      {feature.category
                        ? feature.category.charAt(0).toUpperCase() + feature.category.slice(1)
                        : 'Unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-xs text-base-content/70 bg-base-200 px-2 py-1 rounded">
                        {feature.type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    <div className="space-y-1">
                      <div className="text-xs font-medium">
                        {feature.pricing.defaultCostType === 'flat_fee'
                          ? 'Flat Fee'
                          : 'Percentage Fee'}
                      </div>
                      {feature.pricing.flatFee && (
                        <div>
                          {feature.pricing.flatFee.currency}{' '}
                          {feature.pricing.flatFee.costs?.monthly?.toFixed(2) || '0.00'}/month
                          <span className="text-xs">
                            {' '}
                            ({feature.pricing.flatFee.application.replace('_', ' ')})
                          </span>
                        </div>
                      )}
                      {feature.pricing.percentageFee &&
                        feature.pricing.percentageFee.percentage > 0 && (
                          <div className="text-xs text-base-content/50">
                            {feature.pricing.percentageFee.percentage}% of{' '}
                            {feature.pricing.percentageFee.type.replace('_', ' ')}
                          </div>
                        )}
                      {feature.type === 'usage' &&
                        feature.pricing.overagePrice &&
                        feature.pricing.overagePrice > 0 && (
                          <div className="text-xs text-base-content/50">
                            +{feature.pricing.flatFee?.currency || 'GBP'}{' '}
                            {feature.pricing.overagePrice.toFixed(2)}/unit overage
                          </div>
                        )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-base-content/70">
                    <div className="space-y-1">
                      <div>Default: {feature.config.defaultQuantity || 1}</div>
                      <div>Max: {feature.config.maxQuantity || 'unlimited'}</div>
                      {(feature.config.isCore || feature.config.requiresSetup) && (
                        <div className="flex space-x-1">
                          {feature.config.isCore && (
                            <span className="inline-flex px-1 py-0.5 text-xs font-medium rounded bg-orange-100 text-orange-800">
                              Core
                            </span>
                          )}
                          {feature.config.requiresSetup && (
                            <span className="inline-flex px-1 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                              Setup
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(feature)}
                      className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full transition-colors ${
                        feature.isActive
                          ? 'bg-green-100 text-green-800 hover:bg-green-200'
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {feature.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(feature)}
                        className="text-primary hover:text-primary/80"
                        title="Edit feature"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleClone(feature)}
                        className="text-green-600 hover:text-green-900"
                        title="Clone feature"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(feature.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Delete feature"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredFeatures.length === 0 && (
        <div className="bg-base-200 rounded-lg p-8 text-center">
          <p className="text-base-content/70 mb-4">
            {features.length === 0 ? 'No features found' : 'No features match the current filters'}
          </p>
          {features.length === 0 && (
            <button
              onClick={() => {
                resetForm();
                setShowCreateForm(true);
              }}
              className="bg-primary hover:bg-primary/80 text-white px-4 py-2 rounded-md font-medium transition-colors"
            >
              Create Your First Feature
            </button>
          )}
        </div>
      )}
      {/* Toasts */}
      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};
